import React, { useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, Clock } from 'lucide-react';
import { isBrowser, safeFocus, safeAddEventListener } from '../utils/browserGuards';

const SessionTimeoutModal = ({
  isOpen,
  countdown,
  countdownFormatted,
  isRefreshingToken,
  onStaySignedIn,
  onSignOutNow,
  onClose,
  isDirty = false
}) => {
  const modalRef = useRef(null);
  
  // Focus management for accessibility with browser guards
  useEffect(() => {
    if (isOpen && isBrowser && modalRef.current) {
      safeFocus(modalRef.current);
    }
  }, [isOpen]);
  
  // Handle ESC key with safe event listeners
  useEffect(() => {
    if (!isBrowser || !isOpen) return;
    
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    const cleanupKeyListener = safeAddEventListener(document, 'keydown', handleKeyDown);
    
    // Prevent body scrolling
    if (document.body && document.body.style) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      cleanupKeyListener();
      if (document.body && document.body.style) {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  const isUrgent = countdown <= 30;
  const willAutoSave = countdown <= 60 && isDirty;
  
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="timeout-title"
      aria-describedby="timeout-description"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
        style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
        tabIndex={-1}
      >
        {/* Header with warning icon */}
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 rounded-full ${isUrgent ? 'bg-red-100' : 'bg-orange-100'}`}>
            <AlertTriangle className={`w-6 h-6 ${isUrgent ? 'text-red-600' : 'text-orange-600'}`} />
          </div>
          <h3 id="timeout-title" className="text-lg font-bold text-slate-900">
            Session Timeout Warning
          </h3>
        </div>
        
        {/* Countdown display */}
        <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
          <Clock className="w-5 h-5 text-slate-500" />
          <span className="font-mono text-lg font-bold text-slate-900">
            {countdownFormatted}
          </span>
          <span className="text-slate-600">remaining</span>
        </div>
        
        {/* Main message */}
        <div id="timeout-description" className="mb-6 space-y-2">
          <p className="text-slate-700">
            You're about to be signed out due to inactivity.
          </p>
          
          {isDirty && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-sm">
                {willAutoSave 
                  ? "🔄 Your draft will be saved automatically in 60 seconds."
                  : "💾 Your draft will be saved automatically before sign out."
                }
              </p>
            </div>
          )}
          
          {isUrgent && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium">
                ⚠️ You will be automatically signed out in {countdown} seconds.
              </p>
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            onClick={onStaySignedIn}
            disabled={isRefreshingToken}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {isRefreshingToken ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Refreshing...
              </>
            ) : (
              'Stay Signed In'
            )}
          </Button>
          
          <Button
            onClick={onSignOutNow}
            variant="outline"
            disabled={isRefreshingToken}
            className="flex-1 text-slate-600 border-slate-200 hover:bg-slate-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Sign Out Now
          </Button>
        </div>
        
        {/* Help text */}
        <div className="mt-4 text-center">
          <button
            onClick={onClose}
            className="text-xs text-slate-500 hover:text-slate-700 underline focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
          >
            Close (auto-save will continue)
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutModal;